import { useEffect, useState, useRef } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Typography,
} from "@mui/material";
import { ArrowBack, Edit, OpenInNew } from "@mui/icons-material";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import api from "../../api";

const QuestionBankPaperViewer = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const listSearch = location.state?.listSearch || "";
  const papersListPath = listSearch
    ? `/admin/question-bank/papers?${listSearch}`
    : "/admin/question-bank/papers";
  const [loading, setLoading] = useState(true);
  const [blobUrl, setBlobUrl] = useState("");
  const [paperName, setPaperName] = useState("");
  const [error, setError] = useState("");
  const blobUrlRef = useRef("");

  useEffect(() => {
    const fetchPaper = async () => {
      setLoading(true);
      setError("");
      try {
        // Fetch PDF as blob - backend now streams from R2 or local
        const res = await api.get(`/admin/question-bank/import/${id}/paper`, {
          responseType: "blob",
        });

        // Extract filename from Content-Disposition header if available
        const disposition = res.headers?.["content-disposition"] || "";
        const filenameMatch = disposition.match(/filename="?([^";\n]+)"?/);
        setPaperName(filenameMatch ? filenameMatch[1] : `Paper #${id}`);

        // Create a local object URL from the blob
        const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
        setBlobUrl(url);
        blobUrlRef.current = url;
      } catch (err) {
        // If the response is a blob containing JSON error, try to parse it
        if (err.response?.data instanceof Blob) {
          try {
            const text = await err.response.data.text();
            const json = JSON.parse(text);
            setError(json.message || "Failed to load PDF paper.");
          } catch {
            setError("Failed to load PDF paper.");
          }
        } else {
          setError(err.response?.data?.message || "Failed to load PDF paper.");
        }
      } finally {
        setLoading(false);
      }
    };
    fetchPaper();

    // Revoke the object URL on unmount to free memory
    return () => {
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
      }
    };
  }, [id]);

  const handleOpenInNewTab = () => {
    if (blobUrl) {
      window.open(blobUrl, "_blank", "noopener,noreferrer");
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Papers (PDF)
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {paperName || "PDF Paper"}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<Edit />}
            onClick={() => navigate(`/admin/question-bank/import/${id}/review`)}
            sx={{ textTransform: "none", borderColor: "#6366f1", color: "#6366f1", "&:hover": { borderColor: "#4f46e5", color: "#4f46e5" } }}
          >
            Edit Questions
          </Button>
          <Button
            variant="outlined"
            startIcon={<ArrowBack />}
            onClick={() => navigate(papersListPath)}
            sx={{ textTransform: "none" }}
          >
            Back to Papers
          </Button>
        </Box>
      </Box>

      <Paper sx={{ p: 2, borderRadius: 2 }}>
        {loading ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 3 }}>
            <CircularProgress size={18} />
            <Typography variant="body2" color="text.secondary">
              Loading PDF...
            </Typography>
          </Box>
        ) : error ? (
          <Typography variant="body2" color="error">
            {error}
          </Typography>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<OpenInNew />}
                onClick={handleOpenInNewTab}
                sx={{ textTransform: "none" }}
              >
                Open PDF in new tab
              </Button>
            </Box>
            <Box
              component="iframe"
              title="PDF Viewer"
              src={blobUrl}
              sx={{
                width: "100%",
                height: "75vh",
                border: "1px solid #e0e0e0",
                borderRadius: 2,
              }}
            />
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default QuestionBankPaperViewer;
